// Top bar — the fixed, opaque game header.
//
//   left   : logo + title, Game menu, Help menu
//   centre : tick/turn + resources, research and faith-crisis actions
//   right  : time controls, theme, language, settings, account
//
// Height is `--topbar-h` (globals.css): one 48px row from `md` up; below
// that the resources wrap onto a second row so a 390px phone still fits.
// Every HUD surface docked under the bar offsets itself by that variable.

import type { TickLoopRunState } from '@aod/sim-adapter';
import { ResourceBar } from '@aod/ui';
import { Button } from '@aod/ui/components/ui/button';
import { FlaskConical, Settings } from 'lucide-react';
import type { UseSession } from '../auth/useSession.js';
import { HelpButton } from '../coachmark/HelpButton.js';
import type { CoachmarkController } from '../coachmark/useCoachmarks.js';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { formatGameTime } from '../sim/gameTime.js';
import { TimeControls } from '../ui/TimeControls.js';
import { AccountMenu } from './topbar/AccountMenu.js';
import { FaithCrisisMenu } from './topbar/FaithCrisisMenu.js';
import { GameMenu } from './topbar/GameMenu.js';
import { LocaleSelect } from './topbar/LocaleSelect.js';
import { ThemeToggle } from './topbar/ThemeToggle.js';
import { TOPBAR_CONTROL_OUTLINE } from './topbar/controlStyle.js';

const RESEARCH_COST = 150;

interface TopBarProps {
  food: number;
  industry: number;
  knowledge: number;
  faith: number;
  treasury: number;
  tick: bigint;
  runState: TickLoopRunState;
  speed: number;
  researchState: { active: boolean; remainingTicks: number } | null;
  faithCrisis: { tension: number; firedEvents?: readonly string[] } | null;
  startResearch: () => void;
  pilgrimage: () => void;
  reform: () => void;
  pause: () => void;
  resume: () => void;
  setSpeed: (multiplier: number) => void;
  /** Highest game speed offered (the human seat's speed-cap assist). */
  maxSpeed?: number;
  onNewMatch?: () => void;
  onSave?: () => void;
  onLoad?: () => void;
  onReturnToMenu?: () => void;
  onOpenSettings: () => void;
  /**
   * Coachmark integration. When `coachmarkController` is provided the Help
   * menu lists every hint topic so the player can re-arm one.
   */
  coachmarkController?: CoachmarkController;
  coachmarkOpen?: boolean;
  onCoachmarkOpenChange?: (next: boolean) => void;
  /** Opens the static HelpModal from the Help menu. */
  onOpenReferenceHelp?: () => void;
  /** Legacy alias for `onOpenReferenceHelp` (multiplayer view). */
  onOpenHelp?: () => void;
  /** Injected in tests; defaults to the live forge-auth session. */
  session?: UseSession;
}

export function TopBar({
  food,
  industry,
  knowledge,
  faith,
  treasury,
  tick,
  runState,
  speed,
  researchState,
  faithCrisis,
  startResearch,
  pilgrimage,
  reform,
  pause,
  resume,
  setSpeed,
  maxSpeed,
  onNewMatch,
  onSave,
  onLoad,
  onReturnToMenu,
  onOpenSettings,
  coachmarkController,
  coachmarkOpen,
  onCoachmarkOpenChange,
  onOpenReferenceHelp,
  onOpenHelp,
  session,
}: TopBarProps) {
  const t = useT();

  return (
    <header
      data-testid="topbar"
      {...appDom({ id: 'topbar', type: 'Header', scope: 'header', label: 'Game top bar' })}
      className="fixed inset-x-0 top-0 z-[45] flex h-(--topbar-h) flex-wrap items-center border-b border-border bg-card px-2 text-card-foreground shadow-sm sm:px-3"
    >
      {/* Left: brand + menus */}
      <div className="order-1 flex h-12 shrink-0 items-center gap-1 pr-2">
        <img
          src="/brand/logo-64.webp"
          srcSet="/brand/logo-64.webp 1x, /brand/logo-128.webp 2x"
          width={28}
          height={28}
          data-testid="topbar-logo"
          {...appDom({ id: 'topbar-logo', type: 'LogoMark', role: 'image', scope: 'header' })}
          alt={t('app.logoAlt')}
          className="size-7 shrink-0 rounded-md"
        />
        <span
          data-testid="topbar-title"
          {...appDom({ id: 'topbar-title', type: 'Brand', role: 'text', scope: 'header' })}
          className="hidden whitespace-nowrap pl-1.5 pr-2 font-semibold tracking-wide text-[15px] 2xl:inline"
        >
          {t('app.title')}
        </span>
        <span aria-hidden className="mx-1 hidden h-5 w-px bg-border 2xl:block" />
        <nav
          className="flex items-center gap-0.5"
          aria-label={t('menu.game')}
          data-testid="topbar-menus"
          {...appDom({ id: 'topbar-menus', type: 'NavBar', scope: 'header' })}
        >
          <GameMenu
            onNewMatch={onNewMatch}
            onSave={onSave}
            onLoad={onLoad}
            onReturnToMenu={onReturnToMenu}
          />
          <HelpButton
            controller={coachmarkController}
            open={coachmarkOpen ?? false}
            onOpenChange={onCoachmarkOpenChange ?? (() => {})}
            onOpenReferenceHelp={onOpenReferenceHelp ?? onOpenHelp}
          />
        </nav>
      </div>

      {/* Centre: resources. Full-width second row below md. The strip is
          the `end-turn` coachmark target (the hint itself docks
          bottom-right) — the tick counter is the visible signal that the
          simulation advances on its own. */}
      <fieldset
        aria-label={t('resources.label')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a sideways-scrolling strip must be keyboard reachable
        tabIndex={0}
        data-testid="topbar-resources"
        {...appDom({ id: 'topbar-resources', type: 'KPI', role: 'container', scope: 'header' })}
        // Scrolls sideways when it can't fit; the faded edges say so.
        className="order-3 m-0 flex h-9 w-full min-w-0 items-center overflow-x-auto border-0 border-t border-border p-0 [mask-image:linear-gradient(to_right,transparent,black_12px,black_calc(100%-16px),transparent)] [scrollbar-width:none] md:order-2 md:h-12 md:w-auto md:flex-1 md:border-t-0 md:[mask-image:none] [&::-webkit-scrollbar]:hidden"
      >
        <div data-coachmark-target="end-turn" className="mx-auto flex w-max items-center">
          <ResourceBar
            food={food}
            industry={industry}
            knowledge={knowledge}
            faith={faith}
            treasury={treasury}
            tickLabel={formatGameTime(tick)}
            className="w-max flex-nowrap gap-0 border-0 bg-transparent px-0 py-0 text-[13px] backdrop-blur-none [&>span]:px-1.5 lg:[&>span]:px-2"
          >
            {researchState?.active ? (
              <span className="ml-1 whitespace-nowrap rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                {t('resources.researchActive', { ticks: researchState.remainingTicks })}
              </span>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="ml-1 h-7 px-2 text-xs"
                data-testid="research-button"
                {...appDom({
                  id: 'research-button',
                  type: 'Button',
                  role: 'button',
                  scope: 'header',
                })}
                onClick={startResearch}
                disabled={knowledge < RESEARCH_COST}
                title={t('resources.researchTitle')}
                aria-label={t('resources.research', { cost: RESEARCH_COST })}
              >
                <FlaskConical className="size-3.5 sm:hidden" aria-hidden />
                <span className="hidden sm:inline">
                  {t('resources.research', { cost: RESEARCH_COST })}
                </span>
              </Button>
            )}
            {faithCrisis && faithCrisis.tension > 0 && (
              <FaithCrisisMenu
                tension={faithCrisis.tension}
                firedEvents={faithCrisis.firedEvents}
                canPilgrimage={treasury >= 100}
                canReform={faith >= 50}
                onPilgrimage={pilgrimage}
                onReform={reform}
              />
            )}
          </ResourceBar>
        </div>
      </fieldset>

      {/* Right: time, preferences, account */}
      <div className="order-2 ml-auto flex h-12 shrink-0 items-center gap-1 pl-2 md:order-3 md:ml-0">
        <TimeControls
          runState={runState}
          speed={speed}
          onPause={pause}
          onResume={resume}
          onSetSpeed={setSpeed}
          maxSpeed={maxSpeed}
          className="mr-1"
        />
        <ThemeToggle />
        <LocaleSelect />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="settings-button"
          {...appDom({
            id: 'settings-button',
            type: 'IconButton',
            role: 'button',
            scope: 'header',
          })}
          onClick={onOpenSettings}
          aria-label={t('settings.open')}
          title={t('settings.open')}
          className={`size-8 ${TOPBAR_CONTROL_OUTLINE}`}
        >
          <Settings className="size-4" />
        </Button>
        <AccountMenu onOpenSettings={onOpenSettings} session={session} />
      </div>
    </header>
  );
}
